// @flow

import { useEffect, useCallback, useRef } from 'react';
import { useReportRenderActions } from '../contexts/report-render-context';
import { useIsMounted } from '../components/widgets/common/hooks';
import type { WidgetData } from '@auravisionlabs/aura-pkg-report-widgets';

/**
 * Hook for widgets to integrate with ReportRenderContext.
 *
 * Usage in a widget:
 *
 * const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);
 *
 * // In loadWidgetData:
 * reportLoading();
 * try {
 *   const data = await fetchData();
 *   reportComplete(transformToWidgetData(data));
 * } catch (e) {
 *   reportError(e.message);
 * }
 */
export const useWidgetRenderState = (widgetId: ?string) => {
  const context = useReportRenderActions();
  const isMounted = useIsMounted();

  // Use refs to store context functions to avoid triggering effects on context changes
  const contextRef = useRef(context);
  contextRef.current = context;
  
  // Track if we've already registered to avoid duplicate registrations
  const registeredRef = useRef(false);

  // Register on mount, unregister on unmount
  // Use empty deps - only run once on mount/unmount
  useEffect(() => {
    if (widgetId && contextRef.current && !registeredRef.current) {
      registeredRef.current = true;
      contextRef.current.registerWidget(widgetId);
      return () => {
        if (contextRef.current) {
          contextRef.current.unregisterWidget(widgetId);
        }
        registeredRef.current = false;
      };
    }
  }, [widgetId]);

  const reportLoading = useCallback(() => {
    if (widgetId && contextRef.current && isMounted()) {
      contextRef.current.setWidgetLoading(widgetId);
    }
  }, [widgetId, isMounted]);

  const reportComplete = useCallback(
    (data: WidgetData) => {
      if (widgetId && contextRef.current && isMounted()) {
        contextRef.current.setWidgetComplete(widgetId, data);
      }
    },
    [widgetId, isMounted]
  );

  const reportError = useCallback(
    (error: string) => {
      if (widgetId && contextRef.current && isMounted()) {
        contextRef.current.setWidgetError(widgetId, error);
      }
    },
    [widgetId, isMounted]
  );

  return {
    reportLoading,
    reportComplete,
    reportError,
  };
};
